import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { userService, leaveService } from '../services/api.service';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectOption } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import AppLayout from '../layouts/AppLayout';
import { useAuth } from '../contexts/AuthContext';

const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Employee data states
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    manager_id: '',
    birthdate: null,
    join_date: null,
    leave_balance: 0,
    sick_leave_balance: 10 // Add default sick leave balance
  });
  
  // Leave history state
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  
  // Leave stats summary
  const [leaveSummary, setLeaveSummary] = useState({
    regularTotal: 0,
    regularUsed: 0,
    sickTotal: 0,
    sickUsed: 0
  });
  
  // Additional state for leave balance adjustment
  const [leaveAdjustment, setLeaveAdjustment] = useState({
    amount: 0,
    reason: ''
  });
  
  // Additional state for sick leave balance adjustment
  const [sickLeaveAdjustment, setSickLeaveAdjustment] = useState({
    amount: 0,
    reason: ''
  });
  
  // Additional state for sick leave
  const [sickLeave, setSickLeave] = useState({
    startDate: null,
    endDate: null,
    reason: '',
    leaveTypeId: ''
  });
  
  // Calculate leave summary from history
  const calculateLeaveSummary = (history) => {
    let regularUsed = 0;
    let sickUsed = 0;
    
    // Get sick leave type IDs
    const sickLeaveTypeIds = leaveTypes
      .filter(type => type.name.toLowerCase().includes('sick'))
      .map(type => type.id);
    
    // Calculate used leave days by type
    history.forEach(leave => {
      // Skip adjustments
      if (leave.is_adjustment) return;
      
      // Skip non-approved leaves
      if (leave.status !== 'approved') return;
      
      // Get working days
      const days = Number(leave.working_days) || 
                   calculateWorkingDays(leave.start_date, leave.end_date);
      
      // Check if it's sick leave
      const isSickLeave = sickLeaveTypeIds.includes(Number(leave.leave_type_id));
      
      if (isSickLeave) {
        sickUsed += days;
      } else {
        regularUsed += days;
      }
    });
    
    return {
      regularTotal: 20, // Standard allocation
      regularUsed,
      sickTotal: 10, // Standard sick leave allocation
      sickUsed
    };
  };
  
  // Fetch user data and related info
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        
        const [userResponse, managerResponse, departmentsResponse, leaveHistoryResponse, leaveTypesResponse] = await Promise.all([
          userService.getUserById(`${id}?_t=${timestamp}`),
          userService.getManagers(`?_t=${timestamp}`),
          userService.getDepartments(`?_t=${timestamp}`),
          leaveService.getUserLeaveRequests(id, `?_t=${timestamp}`),
          leaveService.getLeaveTypes(`?_t=${timestamp}`)
        ]);
        
        // Format dates
        const user = userResponse.user;
        if (user.birthdate) {
          user.birthdate = new Date(user.birthdate);
        }
        if (user.join_date) {
          user.join_date = new Date(user.join_date);
        }
        
        // Add sick leave balance if not present
        if (user.sick_leave_balance === undefined) {
          user.sick_leave_balance = 10; // Default value if not provided by API
        }
        
        setUserData(user);
        setManagers(managerResponse.managers || []);
        setDepartments(departmentsResponse.departments || []);
        
        // Sort leave history by date (newest first)
        const sortedLeaveHistory = (leaveHistoryResponse.leaveRequests || []).sort((a, b) => {
          return new Date(b.start_date) - new Date(a.start_date);
        });
        
        setLeaveHistory(sortedLeaveHistory);
        
        // Set leave types
        if (leaveTypesResponse && Array.isArray(leaveTypesResponse.leaveTypes)) {
          setLeaveTypes(leaveTypesResponse.leaveTypes);
          
          // Find sick leave type and set it as default
          const sickLeaveType = leaveTypesResponse.leaveTypes.find(type => 
            type.name.toLowerCase().includes('sick'));
          
          if (sickLeaveType) {
            setSickLeave(prev => ({
              ...prev,
              leaveTypeId: sickLeaveType.id.toString()
            }));
          }
          
          // Calculate leave summary
          const summary = calculateLeaveSummary(sortedLeaveHistory);
          setLeaveSummary(summary);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user information');
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle date changes
  const handleDateChange = (name, date) => {
    setUserData(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  // Handle sick leave date changes
  const handleSickLeaveDateChange = (name, date) => {
    setSickLeave(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  // Handle sick leave input changes
  const handleSickLeaveChange = (e) => {
    const { name, value } = e.target;
    setSickLeave(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle leave adjustment input
  const handleAdjustmentChange = (e) => {
    const { name, value } = e.target;
    setLeaveAdjustment(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseInt(value) || 0 : value
    }));
  };
  
  // Handle sick leave adjustment input
  const handleSickAdjustmentChange = (e) => {
    const { name, value } = e.target;
    setSickLeaveAdjustment(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseInt(value) || 0 : value
    }));
  };
  
  // Submit user data updates
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!userData.name || !userData.email) {
      setError('Name and email are required');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format dates for API
      const formattedUserData = {
        ...userData,
        birthdate: userData.birthdate ? userData.birthdate.toISOString().split('T')[0] : null,
        join_date: userData.join_date ? userData.join_date.toISOString().split('T')[0] : null
      };
      
      await userService.updateUser(id, formattedUserData);
      
      // Notify other components that user data has been updated
      localStorage.setItem('leave_balance_updated', 'true');
      
      setSuccess('User information updated successfully');
      setIsSubmitting(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update user';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  // Apply leave balance adjustment
  const handleLeaveAdjustment = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (leaveAdjustment.amount === 0) {
      setError('Please enter a non-zero adjustment amount');
      return;
    }
    
    if (!leaveAdjustment.reason.trim()) {
      setError('Please provide a reason for the adjustment');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await leaveService.adjustLeaveBalance(id, {
        amount: leaveAdjustment.amount,
        reason: leaveAdjustment.reason,
        isAnnualLeave: true // Specify this is for annual leave
      });
      
      // Update the user data with the new balance
      setUserData(prev => ({
        ...prev,
        leave_balance: prev.leave_balance + leaveAdjustment.amount
      }));
      
      // Reset the adjustment form
      setLeaveAdjustment({
        amount: 0,
        reason: ''
      });
      
      setSuccess('Leave balance adjusted successfully');
      
      // Refresh leave history
      await refreshLeaveHistory();
      
      // Set flag in localStorage to trigger dashboard refresh
      localStorage.setItem('leave_balance_updated', 'true');
      
      // Trigger a custom event that can be listened for by other components
      window.dispatchEvent(new CustomEvent('leave-balance-update', {
        detail: { userId: id }
      }));
      
      setIsSubmitting(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to adjust leave balance';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  // Apply sick leave balance adjustment
  const handleSickLeaveAdjustment = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (sickLeaveAdjustment.amount === 0) {
      setError('Please enter a non-zero adjustment amount');
      return;
    }
    
    if (!sickLeaveAdjustment.reason.trim()) {
      setError('Please provide a reason for the adjustment');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await leaveService.adjustLeaveBalance(id, {
        amount: sickLeaveAdjustment.amount,
        reason: sickLeaveAdjustment.reason,
        isSickLeave: true // Specify this is for sick leave
      });
      
      // Update the user data with the new sick leave balance
      setUserData(prev => ({
        ...prev,
        sick_leave_balance: (prev.sick_leave_balance || 10) + sickLeaveAdjustment.amount
      }));
      
      // Reset the adjustment form
      setSickLeaveAdjustment({
        amount: 0,
        reason: ''
      });
      
      setSuccess('Sick leave balance adjusted successfully');
      
      // Refresh leave history
      await refreshLeaveHistory();
      
      // Set flag in localStorage to trigger dashboard refresh
      localStorage.setItem('leave_balance_updated', 'true');
      
      // Trigger a custom event that can be listened for by other components
      window.dispatchEvent(new CustomEvent('leave-balance-update', {
        detail: { userId: id }
      }));
      
      setIsSubmitting(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to adjust sick leave balance';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  // Submit sick leave
  const handleSickLeaveSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate input
    if (!sickLeave.startDate || !sickLeave.endDate) {
      setError('Please select both start and end dates for sick leave');
      return;
    }
    
    if (!sickLeave.leaveTypeId) {
      setError('Please select a leave type');
      return;
    }
    
    if (!sickLeave.reason.trim()) {
      setError('Please provide a reason for the sick leave');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format dates for API
      const formatDateForAPI = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };
      
      // Create the leave request
      const leaveRequest = {
        startDate: formatDateForAPI(sickLeave.startDate),
        endDate: formatDateForAPI(sickLeave.endDate),
        leaveTypeId: parseInt(sickLeave.leaveTypeId),
        reason: sickLeave.reason,
        userId: parseInt(id), // Important: set the user ID
        isSickLeave: true // Specify this is sick leave so it doesn't deduct from annual leave
      };
      
      // Use admin's ability to create leave request on behalf of the user
      await leaveService.createLeaveRequestForUser(leaveRequest);
      
      // Reset the form
      setSickLeave({
        ...sickLeave,
        startDate: null,
        endDate: null,
        reason: ''
      });
      
      setSuccess('Sick leave added successfully');
      
      // Refresh leave history
      await refreshLeaveHistory();
      
      // Notify other components of the change
      localStorage.setItem('leave_balance_updated', 'true');
      window.dispatchEvent(new CustomEvent('leave-balance-update', {
        detail: { userId: id }
      }));
      
      setIsSubmitting(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to add sick leave';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  // Refresh leave history
  const refreshLeaveHistory = async () => {
    try {
      const timestamp = new Date().getTime();
      const [leaveHistoryResponse, userResponse] = await Promise.all([
        leaveService.getUserLeaveRequests(id, `?_t=${timestamp}`),
        userService.getUserById(`${id}?_t=${timestamp}`)
      ]);
      
      // Sort leave history by date (newest first)
      const sortedLeaveHistory = (leaveHistoryResponse.leaveRequests || []).sort((a, b) => {
        return new Date(b.start_date) - new Date(a.start_date);
      });
      
      setLeaveHistory(sortedLeaveHistory);
      
      // Update user data with fresh leave balances
      if (userResponse && userResponse.user) {
        setUserData(prev => ({
          ...prev,
          leave_balance: userResponse.user.leave_balance,
          sick_leave_balance: userResponse.user.sick_leave_balance || prev.sick_leave_balance
        }));
      }
      
      // Recalculate leave summary
      const summary = calculateLeaveSummary(sortedLeaveHistory);
      setLeaveSummary(summary);
      
      return true;
    } catch (err) {
      console.error('Error refreshing leave history:', err);
      return false;
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Calculate working days between dates manually if not provided
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Set to noon to avoid timezone issues
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      
      // Calculate business days between dates
      let days = 0;
      let current = new Date(start);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          days++;
        }
        
        // Move to next day
        current.setDate(current.getDate() + 1);
      }
      
      return days;
    } catch (e) {
      console.error('Error calculating working days:', e);
      return '-';
    }
  };
  
  // Check if a leave is sick leave
  const isSickLeave = (leave) => {
    if (!leaveTypes.length) return false;
    
    const sickLeaveTypeIds = leaveTypes
      .filter(type => type.name.toLowerCase().includes('sick'))
      .map(type => type.id);
      
    return sickLeaveTypeIds.includes(Number(leave.leave_type_id));
  };
  
  // Get status badge style
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      adjustment: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return styles[status] || 'bg-gray-100 text-gray-800';
  };
  
  // Get leave type badge style
  const getLeaveTypeBadge = (leave) => {
    if (isSickLeave(leave)) {
      return 'bg-rose-100 text-rose-800';
    }
    return 'bg-indigo-100 text-indigo-800';
  };
  
  if (isLoading) {
    return (
      <AppLayout>
        <div className="text-center py-10">Loading user data...</div>
      </AppLayout>
    );
  }
  
  // Check if current user has permission to view/edit
  const hasPermission = currentUser.role === 'admin' || 
                        (currentUser.role === 'manager' && userData.department === currentUser.department);
                        
  if (!hasPermission) {
    return (
      <AppLayout>
        <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
          You don't have permission to view this page
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Edit Employee: {userData.name}</h1>
          
          <div className="space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate('/staff')}
            >
              Back to Staff List
            </Button>
            <Button
              variant="outline"
              onClick={refreshLeaveHistory}
            >
              Refresh Data
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 text-green-600 p-4 rounded-md mb-4">
            {success}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employee Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Employee Information</CardTitle>
            </CardHeader>
            
            <CardContent>
              <form id="userForm" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="name">Full Name</label>
                    <Input
                      id="name"
                      name="name"
                      value={userData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="email">Email Address</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={userData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="role">Role</label>
                    <Select
                      id="role"
                      name="role"
                      value={userData.role}
                      onChange={handleChange}
                      disabled={currentUser.role !== 'admin' || isSubmitting}
                    >
                       <SelectOption value="admin">Admin</SelectOption>
                        <SelectOption value="employee">Employee</SelectOption>
                        <SelectOption value="garden">Garden Staff</SelectOption>
                        <SelectOption value="kitchen">IT</SelectOption>
                        <SelectOption value="kitchen">Kitchen Staff</SelectOption>
                        <SelectOption value="manager">Manager</SelectOption>
                        <SelectOption value="reception">Reception</SelectOption>
                        <SelectOption value="sales">Internal Sales</SelectOption>
                        <SelectOption value="storeroom">Storeroom</SelectOption>
                        <SelectOption value="workshop">Workshop</SelectOption>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="department">Department</label>
                    <Select
                      id="department"
                      name="department"
                      value={userData.department || ''}
                      onChange={handleChange}
                      disabled={currentUser.role !== 'admin' || isSubmitting}
                    >
                      <SelectOption value="" disabled>Select Department</SelectOption>
                      {departments.map(dept => (
                        <SelectOption key={dept} value={dept}>{dept}</SelectOption>
                      ))}
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="manager_id">Manager</label>
                    <Select
                      id="manager_id"
                      name="manager_id"
                      value={userData.manager_id || ''}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    >
                      <SelectOption value="">None</SelectOption>
                      {managers.map(manager => (
                        <SelectOption key={manager.id} value={manager.id.toString()}>
                          {manager.name}
                        </SelectOption>
                      ))}
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Join Date</label>
                    <DatePicker
                      date={userData.join_date}
                      setDate={(date) => handleDateChange('join_date', date)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Date of Birth</label>
                    <DatePicker
                      date={userData.birthdate}
                      setDate={(date) => handleDateChange('birthdate', date)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </form>
            </CardContent>
            
            <CardFooter>
              <Button
                type="submit"
                form="userForm"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardFooter>
          </Card>
          
          {/* Leave Management Section */}
          <Card>
            <CardHeader>
              <CardTitle>Leave Management</CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Annual Leave Balance */}
                <div className="text-center p-4 bg-indigo-50 rounded-md">
                  <h3 className="text-sm text-gray-500 mb-1">Annual Leave</h3>
                  <p className="text-2xl font-bold text-indigo-700">{userData.leave_balance} days</p>
                </div>
                
                {/* Sick Leave Balance */}
                <div className="text-center p-4 bg-rose-50 rounded-md">
                  <h3 className="text-sm text-gray-500 mb-1">Sick Leave</h3>
                  <p className="text-2xl font-bold text-rose-700">{userData.sick_leave_balance || 10} days</p>
                </div>
              </div>
              
              {/* Leave Balance Adjustment Form */}
              <div>
                <h3 className="font-medium mb-2">Adjust Annual Leave Balance</h3>
                <form id="adjustmentForm" onSubmit={handleLeaveAdjustment} className="space-y-3">
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      name="amount"
                      placeholder="Amount (e.g. 5 or -2)"
                      value={leaveAdjustment.amount || ''}
                      onChange={handleAdjustmentChange}
                      disabled={isSubmitting}
                      className="w-1/3"
                    />
                    
                    <Input
                      name="reason"
                      placeholder="Reason for adjustment"
                      value={leaveAdjustment.reason}
                      onChange={handleAdjustmentChange}
                      disabled={isSubmitting}
                      className="flex-1"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    form="adjustmentForm"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Applying...' : 'Adjust Annual Leave'}
                  </Button>
                </form>
              </div>
              
              {/* Sick Leave Adjustment Form */}
              <div className="pt-4 border-t">
                <h3 className="font-medium mb-2">Adjust Sick Leave Balance</h3>
                <form id="sickAdjustmentForm" onSubmit={handleSickLeaveAdjustment} className="space-y-3">
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      name="amount"
                      placeholder="Amount (e.g. 5 or -2)"
                      value={sickLeaveAdjustment.amount || ''}
                      onChange={handleSickAdjustmentChange}
                      disabled={isSubmitting}
                      className="w-1/3"
                    />
                    
                    <Input
                      name="reason"
                      placeholder="Reason for adjustment"
                      value={sickLeaveAdjustment.reason}
                      onChange={handleSickAdjustmentChange}
                      disabled={isSubmitting}
                      className="flex-1"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    form="sickAdjustmentForm"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Applying...' : 'Adjust Sick Leave'}
                  </Button>
                </form>
              </div>
              
              {/* Sick Leave Form - Only visible for admins */}
              {currentUser.role === 'admin' && leaveTypes.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-medium mb-2">Add Sick Leave</h3>
                  <form id="sickLeaveForm" onSubmit={handleSickLeaveSubmit} className="space-y-3">
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">Leave Type</label>
                      <Select
                        id="leaveTypeId"
                        name="leaveTypeId"
                        value={sickLeave.leaveTypeId}
                        onChange={handleSickLeaveChange}
                        disabled={isSubmitting}
                      >
                        <SelectOption value="" disabled>Select Type</SelectOption>
                        {leaveTypes
                          .filter(type => type.name.toLowerCase().includes('sick'))
                          .map(type => (
                            <SelectOption key={type.id} value={type.id.toString()}>
                              {type.name}
                            </SelectOption>
                          ))
                        }
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">Start Date</label>
                      <DatePicker
                        date={sickLeave.startDate}
                        setDate={(date) => handleSickLeaveDateChange('startDate', date)}
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">End Date</label>
                      <DatePicker
                        date={sickLeave.endDate}
                        setDate={(date) => handleSickLeaveDateChange('endDate', date)}
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-xs text-gray-500">Reason</label>
                      <Input
                        name="reason"
                        placeholder="Reason for sick leave"
                        value={sickLeave.reason}
                        onChange={handleSickLeaveChange}
                        disabled={isSubmitting}
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      form="sickLeaveForm"
                      disabled={isSubmitting}
                      className="w-full"
                    >
                      {isSubmitting ? 'Adding...' : 'Add Sick Leave'}
                    </Button>
                  </form>
                  <div className="text-xs text-gray-500 mt-2">
                    <p>Note: Sick leave does not deduct from annual leave balance.</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Leave History */}
          <Card className="lg:col-span-3">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>Leave History</CardTitle>
              <div className="text-sm text-gray-500">
                {leaveHistory.length} records found
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Leave Summary */}
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-indigo-50 rounded-md text-center">
                  <h3 className="text-sm font-medium text-indigo-700">Annual Leave Used</h3>
                  <p className="text-xl font-bold text-indigo-900 mt-1">{leaveSummary.regularUsed} days</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-md text-center">
                  <h3 className="text-sm font-medium text-indigo-700">Annual Leave Remaining</h3>
                  <p className="text-xl font-bold text-indigo-900 mt-1">{userData.leave_balance} days</p>
                </div>
                <div className="p-3 bg-rose-50 rounded-md text-center">
                  <h3 className="text-sm font-medium text-rose-700">Sick Leave Used</h3>
                  <p className="text-xl font-bold text-rose-900 mt-1">{leaveSummary.sickUsed} days</p>
                </div>
                <div className="p-3 bg-rose-50 rounded-md text-center">
                  <h3 className="text-sm font-medium text-rose-700">Sick Leave Remaining</h3>
                  <p className="text-xl font-bold text-rose-900 mt-1">{userData.sick_leave_balance || 10} days</p>
                </div>
              </div>
            
              {leaveHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Period</th>
                        <th className="text-left py-3 px-4">Type</th>
                        <th className="text-left py-3 px-4">Days</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Reason</th>
                        <th className="text-left py-3 px-4">Reviewed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveHistory.map(leave => (
                        <tr key={leave.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${getLeaveTypeBadge(leave)}`}>
                              {leave.leave_type_name || leave.leave_type || 'Standard Leave'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {leave.is_adjustment ? (
                              <span className={Number(leave.adjustment_amount) >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {Number(leave.adjustment_amount) >= 0 ? '+' : ''}{leave.adjustment_amount}
                              </span>
                            ) : (
                              <span>
                                {/* Show working days if available, or calculate */}
                                {leave.working_days && Number(leave.working_days) > 0 
                                  ? leave.working_days 
                                  : calculateWorkingDays(leave.start_date, leave.end_date)} days
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(leave.status)} capitalize`}>
                              {leave.is_adjustment ? 'adjustment' : leave.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {leave.reason || '-'}
                          </td>
                          <td className="py-3 px-4">
                            {leave.reviewer_name || leave.approver_name || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No leave history found
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default EditUserPage;